import { useState, type FormEvent } from "react";
import { ArrowRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "@/lib/locale-link";
import { useTranslation } from "@/hooks/useTranslation";
import { track } from "@/lib/analytics";
import { CONSUMER_JOIN_ROUTE, TRADE_BRAND_ROUTE, TRADE_JOIN_ROUTE, REGISTRY_ROUTE } from "@/lib/homepage-ctas";

// Hero image served via Supabase Storage image-render endpoint so we can
// request appropriately sized variants for srcset. The `/render/image/public/`
// path performs server-side resizing/compression on the original object.
const HERO_BASE =
  "https://vzzifwipsxxzghkauall.supabase.co/storage/v1/render/image/public/media-library/system/1775477249335-gz0qs3a5k5h.png";
const heroSrc = `${HERO_BASE}?width=1280&quality=70`;
const heroSrcSet = [
  `${HERO_BASE}?width=640&quality=65 640w`,
  `${HERO_BASE}?width=960&quality=68 960w`,
  `${HERO_BASE}?width=1280&quality=70 1280w`,
  `${HERO_BASE}?width=1920&quality=72 1920w`,
].join(", ");

export function HeroV2() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [q, setQ] = useState("");

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    const term = q.trim();
    track("hero_cta_clicked", { cta: "search", query: term, destination: "/cigars", surface: "home_desktop_hero", device: "desktop" });
    navigate(term ? `/cigars?q=${encodeURIComponent(term)}` : "/cigars");
  };

  const trustLine = [
    t("heroV2.trustFree"),
    t("heroV2.trustNoCard"),
    t("heroV2.trustPrivate"),
  ];

  return (
    <section
      className="relative isolate min-h-[520px] flex items-end overflow-hidden"
      style={{ backgroundColor: "#2a1f1a" }}
    >
      {/* LCP image — discoverable <img> with fetchpriority="high" so the
          browser preload scanner finds it immediately. */}
      <img
        src={heroSrc}
        srcSet={heroSrcSet}
        sizes="100vw"
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        loading="eager"
        decoding="async"
        width={1920}
        height={1080}
        className="absolute inset-0 -z-10 h-full w-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/25 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent pointer-events-none" />

      <div className="container relative z-10 py-8 md:py-10 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1.45fr_1fr] gap-6 lg:gap-10 items-end">
          {/* ─── Path 1 — Cigar Enthusiasts ─── */}
          <div className="max-w-2xl">
            <span className="editorial-subheading text-primary-foreground/70 tracking-[0.25em]">
              {t("heroV2.eyebrow")}
            </span>
            <h1
              className="font-display font-bold text-white mt-2 text-[30px] leading-[1.1] sm:text-4xl sm:leading-[1.05] md:text-5xl md:leading-[1.05]"
              style={{ textShadow: "0 2px 16px rgba(0,0,0,0.55)" }}
            >
              {t("heroV2.titleLine1")}<br />
              {t("heroV2.titleLine2")}
            </h1>
            <p
              className="mt-3 text-white/85 text-sm md:text-base max-w-xl leading-relaxed"
              style={{ textShadow: "0 1px 8px rgba(0,0,0,0.4)" }}
            >
              {t("heroV2.body")}
            </p>

            {/* Registry search */}
            <form onSubmit={onSearch} className="mt-4 flex w-full max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground/70" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("heroV2.searchPlaceholder")}
                  aria-label={t("heroV2.searchAriaLabel")}
                  className="w-full h-11 md:h-12 pl-12 pr-4 rounded-l-xl bg-background/95 backdrop-blur text-foreground placeholder:text-muted-foreground/60 border border-r-0 border-border/40 focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <Button
                type="submit"
                size="lg"
                className="h-11 md:h-12 rounded-l-none rounded-r-xl px-4 md:px-7 font-medium"
              >
                {t("heroV2.searchButton")}
              </Button>
            </form>

            {/* CTAs */}
            <div className="mt-4 flex flex-col sm:flex-row gap-2 md:gap-3">
              <Link
                to={CONSUMER_JOIN_ROUTE}
                onClick={() =>
                  track("hero_cta_clicked", {
                    cta: "consumer_join",
                    intent: "consumer_join",
                    destination: CONSUMER_JOIN_ROUTE,
                    surface: "home_desktop_hero",
                    device: "desktop",
                  })
                }
              >
                <Button size="lg" className="w-full sm:w-auto gap-2 min-h-[48px] font-medium px-6">
                  {t("heroV2.ctaPrimary")} <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link
                to={REGISTRY_ROUTE}
                onClick={() =>
                  track("hero_cta_clicked", {
                    cta: "registry_explore",
                    intent: "registry_explore",
                    destination: REGISTRY_ROUTE,
                    surface: "home_desktop_hero",
                    device: "desktop",
                  })
                }
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto min-h-[48px] font-medium px-6 bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white"
                >
                  {t("heroV2.ctaSecondary")}
                </Button>
              </Link>
            </div>

            <p className="mt-3 text-xs md:text-sm text-white/70">{t("heroV2.microcopy")}</p>

            {/* Consumer journey progression + trust line */}
            <p className="mt-4 font-display text-base md:text-lg text-white/90">
              {t("heroV2.progression")}
            </p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5 text-xs md:text-sm text-white/70">
              {trustLine.map((label, i) => (
                <span key={label} className="flex items-center gap-2">
                  {i > 0 && <span className="h-1 w-1 rounded-full bg-white/40" aria-hidden />}
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* ─── Path 2 — Brands and the Cigar Trade ─── */}
          <aside className="rounded-2xl border border-white/15 bg-white/[0.07] backdrop-blur px-5 py-6 lg:px-6 lg:py-7">
            <span className="editorial-subheading text-primary-foreground/70 tracking-[0.2em] text-[10px]">
              {t("heroTrade.eyebrow")}
            </span>
            <h2 className="font-display text-lg lg:text-xl font-semibold text-white mt-2 leading-snug">
              {t("heroTrade.title")}
            </h2>
            <p className="text-sm text-white/75 mt-2 leading-relaxed">{t("heroTrade.body")}</p>

            <Link
              to={TRADE_JOIN_ROUTE}
              className="block mt-4"
              onClick={() =>
                track("audience_cta_clicked", {
                  audience: "trade",
                  intent: "trade_join",
                  destination: TRADE_JOIN_ROUTE,
                  surface: "home_desktop_hero_trade",
                  device: "desktop",
                })
              }
            >
              <Button
                size="lg"
                variant="outline"
                className="w-full min-h-[48px] gap-2 font-medium bg-white/95 text-primary border-transparent hover:bg-white hover:text-primary"
              >
                {t("heroTrade.cta")} <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>

            <div className="mt-3 flex flex-col gap-1.5 text-sm">
              <Link
                to={TRADE_BRAND_ROUTE}
                className="text-white/80 underline underline-offset-4 decoration-white/30 hover:text-white hover:decoration-white transition-colors"
                onClick={() =>
                  track("audience_cta_clicked", {
                    audience: "trade",
                    intent: "brand_join",
                    destination: TRADE_BRAND_ROUTE,
                    surface: "home_desktop_hero_trade",
                    device: "desktop",
                  })
                }
              >
                {t("heroTrade.brandLink")}
              </Link>
              <Link
                to={TRADE_JOIN_ROUTE}
                className="text-white/80 underline underline-offset-4 decoration-white/30 hover:text-white hover:decoration-white transition-colors"
                onClick={() =>
                  track("audience_cta_clicked", {
                    audience: "trade",
                    intent: "trade_join",
                    destination: TRADE_JOIN_ROUTE,
                    surface: "home_desktop_hero_trade_list",
                    device: "desktop",
                  })
                }
              >
                {t("heroTrade.tradeLink")}
              </Link>
            </div>

            <p className="mt-3 text-xs text-white/60 leading-relaxed">{t("heroTrade.microcopy")}</p>
          </aside>
        </div>
      </div>
    </section>
  );
}
